import {css, Typography, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  DlMuiTypography,
  ENABLE_PH_REC,
  formattedPropertyModel,
  GetStylesFunction,
  NoDataMessageWithIcon,
  TypeWithEtag,
  utils,
} from '@wallaby-ui/core';
import {parseISO} from 'date-fns';
import {isEmpty} from 'lodash';
import * as React from 'react';
import {useMemo} from 'react';
import {useIntl} from 'react-intl';

import {AtRiskEdit} from './AtRiskEdit';
import {EvaluationStateEdit} from './EvaluationStateEdit';
import {PatientCardItemWrapper} from './PatientCardItemWrapper';
import {PatientCommentsEdit} from './PatientCommentsEdit';
import {useFormatters} from 'src/js/common';
import {MerieSchema} from 'src/js/common/types';

interface OwnProps {
  storageIdPrefix: string;
  patientCardData: TypeWithEtag<MerieSchema.components['schemas']['PatientCardDto']>;
  reloadPatientCard: () => void;
  config: ConfigObjectType;
  isCommentsMaximized: boolean;
  setIsCommentsMaximized: (newValue: boolean) => void;
  comment?: string;
  commentFormatted?: string;
  setComment: (comment: string | undefined) => void;
  setCommentFormatted: (commentFormatted: string | undefined) => void;
}

type Props = OwnProps;

export const PatientCardV2: React.FC<Props> = (props) => {
  const {
    storageIdPrefix,
    patientCardData,
    reloadPatientCard,
    config,
    isCommentsMaximized,
    setIsCommentsMaximized,
    comment,
    commentFormatted,
    setComment,
    setCommentFormatted,
  } = props;

  const theme = useTheme();
  const styles = getStyles({theme});
  const intl = useIntl();

  const propDefinition = useMemo(() => {
    return new formattedPropertyModel.FormattedProperty({
      type: formattedPropertyModel.FORMATTED_PROPERTY_TYPE_DECIMAL,
    });
  }, []);

  const {t} = useFormatters();

  // REFACTOR: use t() instead of intl
  const formattedBmi = formattedPropertyModel.FormattedProperty.formatValue(propDefinition, patientCardData.bmi, intl);
  const formattedHeight = formattedPropertyModel.FormattedProperty.formatValue(propDefinition, patientCardData.height, intl);
  const formattedWeight = formattedPropertyModel.FormattedProperty.formatValue(propDefinition, patientCardData.weight, intl);

  const translatedGender =
    intl && utils.TranslateUtils.translate(patientCardData.genderCode === 'M' ? 'patient.card.male' : 'patient.card.female', intl);

  const translatedYears =
    intl &&
    utils.TranslateUtils.translate(
      {
        id: 'patient.card.yearCount',
        defaultMessage: '{yearsCount, plural, one {# year} few {# years}  other {# years}}',
      },
      intl,
      {
        yearsCount: patientCardData.age,
      }
    );

  const genderPatientInfo = patientCardData.genderCode ? `${translatedGender}` : undefined;
  const yearsOldPatientInfo = patientCardData.age ? `${patientCardData.genderCode ? ', ' : undefined}${translatedYears}` : undefined;
  const heightPatientInfo = patientCardData.height ? `${formattedHeight} cm` : undefined;
  const weightPatientInfo = patientCardData.weight ? `${formattedWeight} kg` : undefined;
  const bmiPatientInfo = patientCardData.bmi ? `${patientCardData.weight ? ', ' : ''}BMI ${formattedBmi}` : undefined;

  const isPatientInfoEmpty =
    !patientCardData.bmi && !patientCardData.weight && !patientCardData.age && !patientCardData.genderCode && !patientCardData.height;

  const lastTransferDateFormatted = patientCardData.currentTransferHappenedAt
    ? utils.DateUtils.getFormattedDate(parseISO(patientCardData.currentTransferHappenedAt), intl, true, true)
    : '';

  return (
    <div css={styles.contentWrapper}>
      {!isCommentsMaximized && (
        <>
          <PatientCardItemWrapper iconTitle={'patient.card.patientInfoIcon'} iconName={'patientClipIcon'} data-name='gender'>
            {isPatientInfoEmpty ? (
              <NoDataMessageWithIcon message={'patient.card.noPatientData'} />
            ) : (
              <div css={styles.patientInfo}>
                <div css={styles.patientInfoRow}>
                  <DlMuiTypography value={genderPatientInfo} variant={'value'} />
                  <DlMuiTypography value={yearsOldPatientInfo} variant={'value'} />
                </div>
                <DlMuiTypography value={heightPatientInfo} variant={'value'} />
                <div css={styles.patientInfoRow}>
                  <DlMuiTypography value={weightPatientInfo} variant={'value'} />
                  <DlMuiTypography value={bmiPatientInfo} variant={'value'} />
                </div>
              </div>
            )}
          </PatientCardItemWrapper>
          {patientCardData.currentTransferDepartment && (
            <PatientCardItemWrapper iconTitle={'patient.card.patientTransferIcon'} iconName={'transferIcon'} data-name='transferDepartment'>
              <Typography css={styles.patientInfo} variant={'value'}>
                {patientCardData.currentTransferDepartment ? <div>{patientCardData.currentTransferDepartment}</div> : null}
                {lastTransferDateFormatted ? <div>{lastTransferDateFormatted}</div> : null}
              </Typography>
            </PatientCardItemWrapper>
          )}
          {!ENABLE_PH_REC && (
            <PatientCardItemWrapper
              iconTitle={'patient.card.patientCheckIcon'}
              iconName={'checkClipIcon'}
              data-name='evaluation-state-wrapper'
            >
              <EvaluationStateEdit
                data-name='evaluation-state'
                patientCardEtag={patientCardData._etag}
                data={patientCardData}
                reloadPatientCard={reloadPatientCard}
                config={config}
              />
              <AtRiskEdit
                data-name='at-risk'
                patientCardEtag={patientCardData._etag}
                data={patientCardData}
                reloadPatientCard={reloadPatientCard}
                config={config}
              />
            </PatientCardItemWrapper>
          )}
          <PatientCardItemWrapper iconTitle={'patient.card.diagnosesIcon'} iconName={'plusClipIcon'} data-name='structuredDiagnosis'>
            {isEmpty(patientCardData.structuredDiagnosis) ? (
              <NoDataMessageWithIcon message={'patient.card.noDiagnoses'} />
            ) : (
              <div css={[styles.patientInfo, styles.structuredDiagnosis]}>
                {patientCardData.structuredDiagnosis.map((diagnosis) => {
                  const diagnosisValue = `${diagnosis.code} - ${diagnosis.description || t('global.unknown')}`;
                  return (
                    <DlMuiTypography
                      key={diagnosis.description}
                      value={diagnosisValue}
                      title={diagnosisValue}
                      variant={'value'}
                      inputCss={styles.structuredDiagnosisItem}
                    />
                  );
                })}
              </div>
            )}
          </PatientCardItemWrapper>
        </>
      )}
      <PatientCommentsEdit
        storageIdPrefix={storageIdPrefix}
        patientCardEtag={patientCardData._etag}
        comment={comment}
        commentFormatted={commentFormatted}
        setComment={setComment}
        setCommentFormatted={setCommentFormatted}
        reloadPatientCard={reloadPatientCard}
        config={config}
        data-name='comments'
        isCommentsMaximized={isCommentsMaximized}
        setIsCommentsMaximized={setIsCommentsMaximized}
      />
    </div>
  );
};

PatientCardV2.displayName = 'PatientCardV2';

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
  `,
  patientInfo: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: ${options?.theme?.spacing(1)};
  `,
  patientInfoRow: css`
    display: flex;
    flex-direction: row;
  `,
  patientComments: css`
    align-self: flex-start;
  `,
  structuredDiagnosis: css`
    gap: ${options?.theme?.spacing(1.5)};
  `,
  structuredDiagnosisItem: css`
    line-height: 14px;
  `,
});
